// ABOUTME: Renders an info button on model-selection product cards listing the model's attachments.
// ABOUTME: Read-only: images preview inline, files show a file icon, and every row is a download link.

import type { IProduct } from '@coolshop/cool-model-selection';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    DownloadIcon,
    IconButton,
    InfoOutlinedIcon,
    InsertDriveFileOutlinedIcon,
    ListItem,
    Typography,
} from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';

// Resolved server-side from the prototype `modelAttachments` setting; `url` is already renderable
// (`/api/media/<id>` for PIM media, the absolute URL for an external link).
type TAttachment = {
    id: string;
    name: string;
    url: string;
    isImage: boolean;
};

type ProductWithAttachments = IProduct & {
    attachments?: TAttachment[];
};

const THUMBNAIL_SIZE = 40;

/**
 * Start-action slot for the model-selection product card. Reads the attachments carried on the
 * product and, when there is at least one, renders an info button opening a read-only list of
 * downloadable files. Renders nothing when the model has no attachment.
 */
export function ProductAttachmentsAction(product: IProduct) {
    const { formatMessage } = useIntl();
    const [isOpen, setIsOpen] = useState(false);

    const { attachments } = product as ProductWithAttachments;

    if (!attachments?.length) {
        return null;
    }

    // The card itself is clickable (it opens the configurator), so neither the trigger nor the
    // dialog may let their clicks bubble up to it.
    const stopAndRun = (run: () => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        run();
    };

    return (
        <Box onClick={stopAndRun(() => undefined)}>
            <IconButton
                size="small"
                aria-label={formatMessage({
                    id: 'catalog.attachments.open',
                    defaultMessage: 'Show attachments',
                })}
                data-testid="product-attachments-button"
                onClick={stopAndRun(() => setIsOpen(true))}
            >
                <InfoOutlinedIcon />
            </IconButton>
            <Dialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                maxWidth="sm"
                fullWidth
                data-testid="product-attachments-dialog"
            >
                <DialogTitle>
                    <Typography variant="h4">
                        {formatMessage({
                            id: 'catalog.attachments.title',
                            defaultMessage: 'Attachments',
                        })}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {attachments.map(attachment => (
                        <ListItem
                            key={attachment.id}
                            startAdornment={
                                attachment.isImage ? (
                                    <img
                                        src={attachment.url}
                                        alt={attachment.name}
                                        width={THUMBNAIL_SIZE}
                                        height={THUMBNAIL_SIZE}
                                        style={{ objectFit: 'contain', display: 'block' }}
                                    />
                                ) : (
                                    <InsertDriveFileOutlinedIcon fontSize="small" />
                                )
                            }
                            endAdornment={
                                <a
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={formatMessage(
                                        {
                                            id: 'catalog.attachments.download',
                                            defaultMessage: 'Download {name}',
                                        },
                                        { name: attachment.name },
                                    )}
                                    data-testid={`product-attachment-download-${attachment.id}`}
                                    style={{ display: 'flex', lineHeight: 0 }}
                                >
                                    <Box display="flex" color="primary.main">
                                        <DownloadIcon fontSize="small" />
                                    </Box>
                                </a>
                            }
                        >
                            {attachment.name}
                        </ListItem>
                    ))}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
